import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { listOrders, getOrderStatuses, getOrderById, cloneOrder, deleteOrderById } from '../../../api/ordersApi';
import { useRetryFetch } from '../hooks/useRetryFetch.js';
import StatusBadge from '../components/StatusBadge.jsx';
import OrderDetailPanel from '../components/OrderDetailPanel.jsx';
import Pagination from '../components/Pagination.jsx';
import Spinner from '../components/Spinner.jsx';
import WarningIcon from '../components/WarningIcon.jsx';
import Tooltip from '../components/Tooltip.jsx';
import ConfirmModal from '../../../components/ConfirmModal';
import Dropdown from '../components/Dropdown.jsx';
import { formatCurrency, formatDate, getAgeingText, getAgeingTone } from '../utils/format.js';
import { classifyPortalError } from '../utils/apiError.js';
import { showErrorToast, showSuccessToast } from '../utils/toast.js';
import './MyOrders.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const EMPTY_ORDERS = [];
const CUSTOM_DATE_RANGE = "Custom";
const DATE_RANGES = {
  "Last week": { unit: "days", value: 7 },
  "Last month": { unit: "months", value: 1 },
  "Last 3 months": { unit: "months", value: 3 },
  "Last year": { unit: "years", value: 1 },
  [CUSTOM_DATE_RANGE]: null,
};

const formatDateForAPIRequest = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function CustomDateRangePicker({ onApply, onCancel }) {
  const today = formatDateForAPIRequest(new Date());
  const [createdFrom, setCreatedFrom] = useState(today);
  const [createdTo, setCreatedTo] = useState(today);
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onCancel?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onCancel]);

  return (
    <div ref={pickerRef} className="customer-portal-date-picker">
      <div className="customer-portal-date-picker__inputs">
        <div className="customer-portal-date-picker__field">
          <label className="customer-portal-date-picker__label">FROM</label>
          <input
            type="date"
            value={createdFrom}
            onChange={(e) => setCreatedFrom(e.target.value)}
            className="customer-portal-date-picker__input"
          />
        </div>
        <div className="customer-portal-date-picker__field">
          <label className="customer-portal-date-picker__label">TO</label>
          <input
            type="date"
            value={createdTo}
            min={createdFrom}
            onChange={(e) => setCreatedTo(e.target.value)}
            className="customer-portal-date-picker__input"
          />
        </div>
      </div>
      <div className="customer-portal-date-picker__actions">
        <button type="button" className="customer-portal-date-picker__btn customer-portal-date-picker__btn--cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="customer-portal-date-picker__btn customer-portal-date-picker__btn--apply"
          onClick={() => onApply?.({ createdFrom, createdTo })}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export default function MyOrders() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilters, setStatusFilters] = useState([]);
  const [deleteConfirmOrderId, setDeleteConfirmOrderId] = useState(null);
  const [cloningOrderId, setCloningOrderId] = useState(null);
  const [requestedDateSort, setRequestedDateSort] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({ createdFrom: null, createdTo: null });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  const customRangeLabel =
    dateRange === CUSTOM_DATE_RANGE &&
    customDateRange.createdFrom &&
    customDateRange.createdTo
      ? `${formatDate(customDateRange.createdFrom)} – ${formatDate(customDateRange.createdTo)}`
      : null;

  const getDateRange = (range) => {
    if (!range) return { createdFrom: null, createdTo: null };
    if (range === CUSTOM_DATE_RANGE) {
      return {
        createdFrom: customDateRange.createdFrom,
        createdTo: customDateRange.createdTo,
      };
    }
    const config = DATE_RANGES[range];
    if (!config) return { createdFrom: null, createdTo: null };

    let createdTo = new Date();
    let createdFrom = new Date();

    switch (config.unit) {
      case "days":
        const daysSinceSunday = createdFrom.getDay();
        createdFrom.setDate(createdFrom.getDate() - daysSinceSunday - 7);
        createdTo.setDate(createdTo.getDate() - daysSinceSunday - 1);
        break;

      case "months":
        createdFrom = new Date(createdFrom.getFullYear(), createdFrom.getMonth() - config.value, 1);
        createdTo = new Date(createdTo.getFullYear(), createdTo.getMonth(), 0);
        break;

      case "years":
        createdFrom = new Date(createdFrom.getFullYear() - config.value, 0, 1);
        createdTo = new Date(createdTo.getFullYear() - 1, 11, 31);
        break;

      default:
        return { createdFrom: null, createdTo: null };
    }

    return {
      createdFrom: formatDateForAPIRequest(createdFrom),
      createdTo: formatDateForAPIRequest(createdTo),
    };
  };

  useEffect(() => {
    getOrderStatuses()
      .then((list) => {
        setStatusFilters(['All', ...(Array.isArray(list) ? list.slice(0, 4) : [])]);
      })
      .catch((err) => {
        setStatusFilters(['All', 'Approved', 'Draft', 'Rejected', 'Cancelled']);
        showErrorToast(classifyPortalError(err).message || "Couldn't load order statuses. Please try again.");
      });
  }, []);

  const { data, loading, error, errorMessage, manualRetries, retry, refetch } = useRetryFetch(
    () => {
      const params = { page, limit: pageSize };
      if (search) params.orderSearch = search;
      if (status && status !== 'All') params.status = status;
      const { createdFrom, createdTo } = getDateRange(dateRange);
      if (createdFrom) params.createdFrom = createdFrom;
      if (createdTo) params.createdTo = createdTo;

      return listOrders(params).then((res) => {
        const rawOrders = Array.isArray(res) ? res : (res.orders ?? []);
        return {
          orders: rawOrders.map((o) => ({
            ...o,
            poNumber: o.purchaseOrderId ?? o.purchase_order_id ?? null,
            soNumber: o.referenceOrderId ?? o.soNumber ?? null,
            requestedDate: o.createdAt,
            owner: {
              name: o.customerName ?? '—',
              initials: o.customerName ? o.customerName.charAt(0).toUpperCase() : '?',
            },
          })),
          total: Array.isArray(res) ? rawOrders.length : (res.total ?? 0),
        };
      });
    },
    [search, status, page, pageSize, dateRange, customDateRange]
  );

  const orders = data?.orders ?? EMPTY_ORDERS;
  const total = data?.total ?? 0;

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const sortedOrders = useMemo(() => {
    if (!requestedDateSort) return orders;

    return [...orders].sort((a, b) => {
      const aTime = new Date(a.requestedDate).getTime();
      const bTime = new Date(b.requestedDate).getTime();
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
      return requestedDateSort === 'asc' ? aTime - bTime : bTime - aTime;
    });
  }, [orders, requestedDateSort]);

  const handleClone = async (orderId) => {
    if (cloningOrderId) return;
    setCloningOrderId(orderId);
    try {
      const order = await cloneOrder(orderId);
      showSuccessToast('Order cloned successfully');
      navigate('/customer/orders/new', { state: { duplicateOrder: order } });
    } catch (err) {
      setCloningOrderId(null);
      showErrorToast(classifyPortalError(err).message || "Failed to clone this order. Please try again.");
    }
  };

  const handleDelete = (orderId) => {
    setDeleteConfirmOrderId(orderId);
  };

  const toggleRequestedDateSort = () => {
    setRequestedDateSort((current) => {
      if (current === null) return 'asc';
      if (current === 'asc') return 'desc';
      return null;
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmOrderId) return;
    const id = deleteConfirmOrderId;
    setDeleteConfirmOrderId(null);
    try {
      await deleteOrderById(id);
      showSuccessToast('Order deleted successfully');
      refetch();
    } catch (err) {
      showErrorToast(classifyPortalError(err).message || "Failed to delete this order. Please try again.");
    }
  };

  const handleExpand = (orderId) => {
    setExpandedId((current) => (current === orderId ? null : orderId));
  };

  const {
    data: expandedOrderDetail,
    loading: expandedOrderLoading,
    error: expandedOrderError,
    errorMessage: expandedOrderErrorMessage,
    manualRetries: expandedOrderManualRetries,
    retry: retryExpandedOrder,
  } = useRetryFetch(
    () => (expandedId ? getOrderById(expandedId) : Promise.resolve(null)),
    [expandedId]
  );

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="customer-portal-page-content">
      <div className="customer-portal-orders-header">
        <div>
          <div className="customer-portal-orders-header__title">My Orders</div>
        </div>
        <button
          className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--lg"
          type="button"
          onClick={() => navigate('/customer/orders/new')}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Order
        </button>
      </div>

      <div className="customer-portal-card customer-portal-filter-bar">
        <div className="customer-portal-filter-bar__search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search by PO No, SO No, Order Owner…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <div className="customer-portal-filter-bar__date">
          <Dropdown
            value={dateRange}
            onChange={(v) => {
              if (v === 'All') {
                setDateRange(null);
                setPage(1);
              } else if (v === CUSTOM_DATE_RANGE) {
                setShowCustomDatePicker(true);
              } else {
                setDateRange(v);
                setPage(1);
              }
            }}
            options={['All', ...Object.keys(DATE_RANGES)]}
            placeholder="Date Range"
            width="100%"
            valueLabel={customRangeLabel}
            showValueTooltip
            startAdornment={
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            }
          />
          {showCustomDatePicker && (
            <div className="customer-portal-filter-bar__date-picker-wrapper" onClick={(e) => e.stopPropagation()}>
              <CustomDateRangePicker
                onApply={({ createdFrom, createdTo }) => {
                  setCustomDateRange({ createdFrom, createdTo });
                  setShowCustomDatePicker(false);
                  setDateRange(CUSTOM_DATE_RANGE);
                  setPage(1);
                }}
                onCancel={() => setShowCustomDatePicker(false)}
              />
            </div>
          )}
        </div>
        <div className="customer-portal-filter-bar__fill" />
        <div className="customer-portal-filter-bar__chips">
          {statusFilters.map((s) => (
            <span
              key={s}
              className={`customer-portal-filter-chip${status === s ? ' customer-portal-filter-chip--active' : ''}`}
              onClick={() => { setStatus(s); setPage(1); }}
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      <div className="customer-portal-card customer-portal-orders-full-table">
          <div className="customer-portal-orders-full-table__cols">
            <span /><span className="customer-portal-orders-full-table__left">Order ID</span><span className="customer-portal-orders-full-table__left">PO Number</span><span className="customer-portal-orders-full-table__left">SO No</span>
            <button
              type="button"
              className={`customer-portal-orders-full-table__sortable customer-portal-orders-full-table__center${requestedDateSort ? ' customer-portal-orders-full-table__sortable--active' : ''}`}
              onClick={toggleRequestedDateSort}
              aria-label={requestedDateSort === 'asc' ? 'Sort requested date descending' : requestedDateSort === 'desc' ? 'Clear requested date sort' : 'Sort requested date ascending'}
            >
              <span>Requested Date</span>
              <span className="customer-portal-orders-full-table__sort-icons" aria-hidden="true">
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.75"
                  className={requestedDateSort === 'asc' ? 'customer-portal-is-active' : ''}
                >
                  <polyline points="18 15 12 9 6 15" />
                </svg>
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.75"
                  className={requestedDateSort === 'desc' ? 'customer-portal-is-active' : ''}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            <span className="customer-portal-orders-full-table__right">Total</span><span className="customer-portal-orders-full-table__center">Status</span><span className="customer-portal-orders-full-table__left">Order Owner</span><span className="customer-portal-orders-full-table__center">Actions</span>
          </div>

          {loading && <div className="customer-portal-card__loader"><Spinner size={32} /></div>}

          {error && (
            <div className="customer-portal-card__error">
              <WarningIcon />
              <span>{errorMessage || "Couldn't load your orders"}</span>
              {manualRetries > 0 && (
                <button
                  onClick={retry}
                  className="customer-portal-btn customer-portal-btn--primary customer-portal-btn--sm"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {!loading && !error && orders.length === 0 && <div className="customer-portal-orders-full-table__empty">No orders match your filters.</div>}

          {!loading && !error && sortedOrders.map((order) => {
            const isExpanded = expandedId === order.id;
            return (
              <div className="customer-portal-orders-full-table__group" key={order.id}>
                <div className={`customer-portal-orders-full-table__row${isExpanded ? ' customer-portal-orders-full-table__row--expanded' : ''}`}>
                  <button type="button" className="customer-portal-orders-full-table__expand-btn"
                    onClick={() => handleExpand(order.id)} aria-label={isExpanded ? 'Collapse row' : 'Expand row'}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}>
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                  <Tooltip as="span" className="customer-portal-orders-full-table__id customer-portal-orders-full-table__left">#{order.id}</Tooltip>
                  <Tooltip as="span" className="customer-portal-orders-full-table__po customer-portal-orders-full-table__left">{order.poNumber ?? '—'}</Tooltip>
                  <Tooltip as="span" className="customer-portal-orders-full-table__so customer-portal-orders-full-table__left">{order.soNumber ?? '—'}</Tooltip>
                  <div className="customer-portal-orders-full-table__center">
                    <Tooltip as="div" className="customer-portal-orders-full-table__date">{formatDate(order.requestedDate)}</Tooltip>
                    {order.ageing != null && (
                      <Tooltip as="div" className={`customer-portal-ageing-badge customer-portal-ageing-badge--${getAgeingTone(order.ageing)}`}>
                        {getAgeingText(order.ageing)}
                      </Tooltip>
                    )}
                  </div>
                  <Tooltip as="span" className="customer-portal-orders-full-table__total customer-portal-orders-full-table__right">{formatCurrency(order.total, order.currency)}</Tooltip>
                  <div className="customer-portal-orders-full-table__center"><StatusBadge status={order.status} /></div>
                  <div className="customer-portal-orders-full-table__owner customer-portal-orders-full-table__left">
                    <span className="customer-portal-orders-full-table__avatar">{order.owner?.initials ?? '?'}</span>
                    <Tooltip as="span" className="customer-portal-orders-full-table__owner-name">{order.owner?.name ?? '—'}</Tooltip>
                  </div>
                  <div className="customer-portal-orders-full-table__actions">
                    <button
                      className="customer-portal-icon-btn"
                      type="button"
                      aria-label="View order"
                      onClick={() => navigate(`/customer/orders/${order.id}`)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                    <button
                      className="customer-portal-icon-btn"
                      type="button"
                      aria-label="Clone order"
                      aria-busy={cloningOrderId === order.id}
                      disabled={Boolean(cloningOrderId)}
                      onClick={() => handleClone(order.id)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        <line x1="13" y1="3" x2="18" y2="3" />
                      </svg>
                    </button>
                    {order.status === 'Draft' && (
                      <>
                        <button
                          className="customer-portal-icon-btn customer-portal-icon-btn--pink"
                          type="button"
                          aria-label="Edit order"
                          onClick={() => navigate(`/customer/orders/edit/${order.id}`)}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                          </svg>
                        </button>
                        <button className="customer-portal-icon-btn customer-portal-icon-btn--danger" type="button" aria-label="Delete order" onClick={() => handleDelete(order.id)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <OrderDetailPanel
                    order={order}
                    detail={expandedOrderDetail}
                    loading={expandedOrderLoading}
                    error={expandedOrderError}
                    errorMessage={expandedOrderErrorMessage}
                    manualRetries={expandedOrderManualRetries}
                    onRetry={retryExpandedOrder}
                  />
                )}
              </div>
            );
          })}
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        infoLabel={`${from}–${to} of ${total} orders`}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <ConfirmModal
        open={!!deleteConfirmOrderId}
        title="Delete Order"
        message={`Are you sure you want to delete order #${deleteConfirmOrderId}?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmOrderId(null)}
      />
    </div>
  );
}
